import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Check, X, ArrowLeft, Zap, Users, Shield, Eye, Crown, Gift, Coins, 
  Bot, ArrowRight, HelpCircle, Sparkles, Star, PartyPopper, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SKO 2026 Promotion Configuration
const SKO_PROMO = {
  active: true,
  code: 'SKO2026',
  discount: 50, // 50% off
  endDate: new Date('2026-02-28T23:59:59'),
  eventName: 'ServiceNow SKO 2026',
  eventDates: 'January 20-22, Las Vegas'
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [plans, setPlans] = useState(null);
  const [creditAmount, setCreditAmount] = useState(100);
  const [promoCode, setPromoCode] = useState('');
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Check if promo is still active
  const isPromoActive = SKO_PROMO.active && new Date() < SKO_PROMO.endDate;

  // Countdown timer effect
  useEffect(() => {
    if (!isPromoActive) return;
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = SKO_PROMO.endDate - now;
      
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000); // Update every second
    return () => clearInterval(timer);
  }, [isPromoActive]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/plans`);
        setPlans(response.data.plans);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      }
    };
    fetchPlans();
  }, []);

  const handleSubscribe = async (tier) => {
    if (!user) {
      toast.error('Please sign in to subscribe');
      navigate('/auth');
      return;
    }

    if (tier === 'free') {
      toast.info('You are already on the Free plan');
      return;
    }

    setLoading(tier);
    const token = localStorage.getItem('token');

    // Handle PILOT code internally (no Stripe, no credit card)
    const trimmedPromo = promoCode.trim().toUpperCase();
    if (trimmedPromo === 'PILOT') {
      try {
        const response = await axios.post(
          `${API}/promo/apply`,
          { promo_code: 'PILOT' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(response.data.message);
        if (refreshUser) await refreshUser();
        navigate('/dashboard');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to apply PILOT code');
      } finally {
        setLoading(null);
      }
      return;
    }

    // Standard Stripe checkout for paid subscriptions
    let codeToSend = null;
    if (isPromoActive) {
      codeToSend = SKO_PROMO.code;
    } else if (trimmedPromo) {
      codeToSend = trimmedPromo;
    }

    try {
      const response = await axios.post(
        `${API}/subscriptions/create-checkout`,
        { 
          tier, 
          billing_cycle: billingCycle,
          promo_code: codeToSend
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(null);
    }
  };

  const handleStartTrial = async () => {
    if (!user) {
      toast.error('Please sign in to start your free trial');
      navigate('/auth');
      return;
    }

    setLoading('trial');
    const token = localStorage.getItem('token');

    try {
      await axios.post(
        `${API}/subscriptions/start-trial`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('🎉 14-day free trial activated!');
      if (refreshUser) await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to start trial');
    } finally {
      setLoading(null);
    }
  };

  const handleBuyCredits = async () => {
    if (!user) {
      toast.error('Please sign in to purchase credits');
      navigate('/auth');
      return;
    }

    setLoading('credits');
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(
        `${API}/credits/purchase`,
        { amount: creditAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase credits');
      setLoading(null);
    }
  };

  const currentTier = user?.subscription_tier || 'free';
  
  // User type selection for non-authenticated users
  const [selectedUserType, setSelectedUserType] = useState('rm'); // 'rm', 'pm', 'freelancer'
  
  // Determine if user is RM - either from auth or from selection
  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner' || (!user && (selectedUserType === 'rm' || selectedUserType === 'freelancer'));
  const isFreelancer = user?.company_type === 'freelancer' || (!user && selectedUserType === 'freelancer');
  const itemName = isRM ? 'resource' : 'project';
  const itemNamePlural = isRM ? 'resources' : 'projects';

  const getPriceDisplay = (tier) => {
    if (!plans || !plans[tier]) return { monthly: 0, annual: 0, annualTotal: 0 };
    const plan = plans[tier];
    return {
      monthly: plan.price_monthly / 100,
      annual: plan.price_annual / 100,
      annualTotal: (plan.price_annual * 12) / 100
    };
  };

  // Calculate discounted prices
  const getDiscountedPrice = (price) => {
    if (!isPromoActive) return price;
    return Math.round(price * (1 - SKO_PROMO.discount / 100));
  };

  // Plan configurations
  const planConfigs = [
    {
      tier: 'free',
      name: 'Free',
      tagline: 'Passive Visibility',
      description: 'Be found without searching',
      icon: Eye,
      color: 'slate',
      bgGradient: 'from-slate-50 to-slate-100',
      borderColor: 'border-slate-200',
      bestFor: isRM 
        ? 'Consultants with occasional availability' 
        : 'Companies exploring the marketplace',
      features: [
        { text: '1 active listing', included: true },
        { text: 'Be contacted by others', included: true },
        { text: 'Direct messaging', included: true },
      ],
      limitations: [
        { text: 'AI-powered outbound matching', included: false },
        { text: 'Team workspace', included: false },
      ],
      creditsInfo: null,
    },
    {
      tier: 'starter',
      name: 'Starter',
      tagline: 'Proactive Matching',
      description: 'Find opportunities actively',
      icon: Zap,
      color: 'blue',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      bestFor: isRM 
        ? 'Independent consultants actively seeking projects' 
        : 'Project managers sourcing single resources',
      features: [
        { text: '1 active listing included', included: true },
        { text: 'AI-powered matching', included: true, highlight: true },
        { text: 'Compatibility scoring', included: true },
        { text: 'Pre-booking requests', included: true },
      ],
      limitations: [],
      creditsInfo: { amount: 100, label: '+100 credits / extra activation' },
      hasTrial: true,
      specialBadge: {
        title: '14-Day Free Trial',
        description: 'Try all features risk-free',
        icon: Gift,
        color: 'blue'
      },
    },
    {
      tier: 'advanced',
      name: 'Advanced',
      tagline: 'Team Collaboration',
      description: 'Scale with your team',
      icon: Users,
      color: 'emerald',
      bgGradient: 'from-emerald-50 to-teal-50',
      borderColor: 'border-emerald-300',
      popular: true,
      bestFor: isRM 
        ? 'Consulting firms managing multiple consultants' 
        : 'Teams coordinating multiple project staffing',
      includes: 'Everything in Starter, plus:',
      features: [
        { text: 'Up to 9 active listings', included: true, highlight: true },
        { text: 'Multi-user team workspace', included: true, highlight: true },
        { text: 'Advanced ranking & filtering', included: true },
        { text: 'AI-augmented consultant flag', included: true },
      ],
      creditsInfo: { amount: 75, label: '+75 credits / extra activation' },
    },
    {
      tier: 'professional',
      name: 'Professional',
      tagline: 'Confidentiality & Control',
      description: 'Full discretion for sensitive deals',
      icon: Shield,
      color: 'purple',
      bgGradient: 'from-purple-50 to-violet-50',
      borderColor: 'border-purple-300',
      bestFor: isRM 
        ? 'Strategic firms requiring discretion on availability' 
        : 'Enterprises with confidential staffing needs',
      includes: 'Everything in Advanced, plus:',
      features: [
        { text: 'Up to 20 active listings', included: true, highlight: true },
        { text: 'Anonymous company mode', included: true, highlight: true },
        { text: 'Block specific companies', included: true, highlight: true },
        { text: 'Dedicated phone support', included: true },
      ],
      specialBadge: { text: 'AI Agents as Resources', badge: '2026', icon: Bot, color: 'purple' },
      creditsInfo: { amount: 50, label: '+50 credits / extra activation' },
    }
  ];

  // Common features organized by category - UPDATED with all platform capabilities
  const commonFeaturesCategories = [
    {
      title: 'AI Intelligence',
      icon: '🤖',
      features: [
        'AI-powered matching (GPT-4)',
        'CV & job description AI analysis',
        'Semantic compatibility insights',
        'Geographic distance calculation (100+ cities)'
      ]
    },
    {
      title: 'Interview & Scheduling',
      icon: '📅',
      features: [
        'Google Calendar integration',
        'Microsoft Outlook sync',
        'Automatic 24h & 1h reminders',
        'Interview reschedule & cancel'
      ]
    },
    {
      title: 'Storage & Control',
      icon: '📦',
      features: [
        'Unlimited stored resources & projects',
        'Activation toggle control',
        'Weekly availability refresh reminders'
      ]
    },
    {
      title: 'Collaboration',
      icon: '🤝',
      features: [
        'Direct company-to-company messaging',
        'Contract templates library',
        'Ratings & reviews system',
        'Zero commissions on placements'
      ]
    },
    {
      title: 'Visibility & Profile',
      icon: '🌐',
      features: [
        'Public company pages',
        'Resource & project profiles',
        'CV upload with AI parsing',
        'Certification showcase'
      ]
    },
    {
      title: 'Analytics & Insights',
      icon: '📊',
      features: [
        'Real-time utilization dashboard',
        'Match score breakdown',
        'Activity statistics',
        'Usage analytics'
      ]
    },
    {
      title: 'Communication',
      icon: '📬',
      features: [
        'Email notifications',
        'In-app notifications',
        'Interview confirmations',
        'Status update alerts'
      ]
    },
    {
      title: 'Trust & Security',
      icon: '✅',
      features: [
        'GDPR compliant',
        'Data encryption',
        'Cancel anytime',
        'No setup fees'
      ]
    }
  ];

  // Freelancer-specific pricing - based on official pricing document
  const freelancerPlanConfigs = [
    {
      tier: 'free',
      name: 'Represented Freelance',
      tagline: 'Delegated Commercial',
      description: 'Let us find clients for you',
      icon: Users,
      color: 'emerald',
      bgGradient: 'from-emerald-50 to-teal-50',
      borderColor: 'border-emerald-300',
      popular: true,
      bestFor: 'Freelancers who want visibility without managing sales',
      features: [
        { text: 'Represented by Operating Partner', included: true, highlight: true },
        { text: '€0 forever - No subscription', included: true, highlight: true },
        { text: 'Full visibility to all PMs', included: true },
        { text: 'AI Matching (Inbound opportunities)', included: true },
        { text: '100% of your rate — Keep your Daily Rate', included: true },
      ],
      limitations: [
        { text: 'No direct commercial contact', included: false },
      ],
      creditsInfo: null,
      specialNote: 'Your profile is actively marketed by our Operating Partner',
    },
    {
      tier: 'starter',
      name: 'Independent Freelance',
      tagline: 'Total Autonomy',
      description: 'Full control over your business',
      icon: Zap,
      color: 'blue',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      bestFor: 'Freelancers who want full control over client relationships',
      features: [
        { text: 'Total autonomy - You manage your business', included: true, highlight: true },
        { text: 'Direct contact with clients & partners', included: true, highlight: true },
        { text: '0% markup - Your rate is what clients see', included: true },
        { text: 'AI Matching (Inbound & outbound)', included: true },
        { text: 'Full messaging & contract tools', included: true },
      ],
      limitations: [],
      specialBadge: {
        title: '14-Day Free Trial',
        description: 'Try all features risk-free',
        icon: Gift,
        color: 'blue'
      },
      creditsInfo: null,
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* SKO 2026 Promo Banner with Countdown */}
      {isPromoActive && (
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-3 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvZz48L3N2Zz4=')] opacity-30"></div>
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-3 text-center relative">
            <PartyPopper className="w-5 h-5 text-yellow-300 animate-bounce" />
            <span className="font-bold text-lg">
              🎰 {SKO_PROMO.eventName} Special!
            </span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
              {SKO_PROMO.discount}% OFF ALL PLANS
            </span>
            {/* Dynamic Countdown */}
            <div className="flex items-center gap-1 bg-black/20 px-3 py-1 rounded-full text-sm">
              <span className="text-yellow-300 font-bold">⏱️</span>
              <span className="font-mono font-bold">
                {timeLeft.days}d {String(timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m {String(timeLeft.seconds).padStart(2, '0')}s
              </span>
              <span className="text-white/70 text-xs ml-1">left</span>
            </div>
            <span className="text-sm text-white/90 hidden sm:inline">
              Code: <span className="font-mono font-bold bg-white/20 px-2 py-0.5 rounded">{SKO_PROMO.code}</span>
            </span>
            <Sparkles className="w-5 h-5 text-yellow-300" />
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Navigation button */}
        <Button 
          variant="ghost" 
          className="mb-6 text-gray-600 hover:text-gray-900"
          onClick={() => navigate(user ? '/dashboard' : '/')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {user ? 'Back to Dashboard' : 'Back to Home'}
        </Button>

        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-gray-900">
            Simple, Usage-Based Pricing
          </h1>
          <p className="text-lg text-gray-600 mb-2 max-w-2xl mx-auto">
            Store unlimited {itemNamePlural}. Pay only for active listings.
          </p>
          
          {/* User Type Selector - Only shown for non-authenticated users */}
          {!user && (
            <div className="flex justify-center gap-2 mt-6 mb-4">
              <span className="text-gray-500 self-center mr-2">I am a:</span>
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
                <button
                  onClick={() => setSelectedUserType('rm')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    selectedUserType === 'rm'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="pricing-type-rm"
                >
                  🏢 Resource Manager
                </button>
                <button
                  onClick={() => setSelectedUserType('pm')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    selectedUserType === 'pm'
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="pricing-type-pm"
                >
                  📊 Project Manager
                </button>
                <button
                  onClick={() => setSelectedUserType('freelancer')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    selectedUserType === 'freelancer'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid="pricing-type-freelancer"
                >
                  👤 Freelancer
                </button>
              </div>
            </div>
          )}
          
          {/* Context-specific message */}
          {!user && (
            <p className="text-sm text-gray-500 mb-4">
              {selectedUserType === 'rm' && '💼 Manage consultant portfolios and find projects for your team'}
              {selectedUserType === 'pm' && '🎯 Find qualified ServiceNow consultants for your projects'}
              {selectedUserType === 'freelancer' && '🚀 Showcase your skills and get discovered by top companies'}
            </p>
          )}
          
          {/* Quick explanation */}
          <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
              <Eye className="w-4 h-4 text-slate-600" />
              <span><strong>Passive</strong> = Visible, can be contacted</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full">
              <Zap className="w-4 h-4 text-blue-600" />
              <span><strong>Proactive</strong> = AI finds matches for you</span>
            </div>
          </div>
        </div>

        {/* Current Plan Status */}
        {user && (
          <div className="mb-8 p-4 bg-white rounded-xl border shadow-sm max-w-2xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  user?.pilot_status === 'active' ? 'bg-purple-500' :
                  user?.subscription_status === 'active' ? 'bg-green-500' :
                  user?.subscription_status === 'trial' ? 'bg-yellow-500' : 'bg-gray-400'
                }`} />
                <div>
                  <span className="text-sm text-gray-500">Current plan:</span>
                  <span className="ml-2 font-bold capitalize text-gray-900">{currentTier}</span>
                  {user?.pilot_status === 'active' && (
                    <span className="ml-2 px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700 font-medium">
                      PILOT — Unlimited
                    </span>
                  )}
                  {user?.subscription_status === 'trial' && !user?.pilot_status && (
                    <span className="ml-2 px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-700 font-medium">
                      Trial
                    </span>
                  )}
                </div>
              </div>
              {user?.credit_balance > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-200">
                  <Coins className="w-4 h-4 text-amber-600" />
                  <span className="font-semibold text-amber-800">{user.credit_balance} credits</span>
                </div>
              )}
            </div>
            {user?.pilot_status === 'active' && user?.pilot_expires_at && (
              <div className="mt-3 p-2.5 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-700">
                  <Shield className="w-4 h-4 inline mr-1" />
                  PILOT Program active — Unlimited access until{' '}
                  <strong>{new Date(user.pilot_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <span className={`text-base font-medium transition-colors ${
            billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'
          }`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
            className="relative inline-flex h-8 w-16 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
            style={{ backgroundColor: billingCycle === 'annual' ? '#10b981' : '#d1d5db' }}
            data-testid="billing-toggle"
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
              billingCycle === 'annual' ? 'translate-x-9' : 'translate-x-1'
            }`} />
          </button>
          <div className="flex items-center gap-2">
            <span className={`text-base font-medium transition-colors ${
              billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-400'
            }`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <span className="px-2 py-1 text-xs font-bold rounded-full bg-emerald-100 text-emerald-700">
                SAVE 17%
              </span>
            )}
          </div>
        </div>

        {/* Promo Code Input */}
        {user && (
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="promo-code-section">
            <div className="flex items-center bg-gray-50 border rounded-lg overflow-hidden">
              <span className="px-3 text-gray-400 text-sm">
                <Gift className="w-4 h-4" />
              </span>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Promo code"
                className="py-2 pr-3 text-sm bg-transparent outline-none w-36"
                data-testid="promo-code-input"
              />
            </div>
            {promoCode.trim().toUpperCase() === 'PILOT' && (
              <span className="text-xs text-green-600">30-day free Advanced access</span>
            )}
          </div>
        )}

        {/* Plans Grid */}
        <div className={`grid grid-cols-1 gap-5 mb-12 ${
          isFreelancer 
            ? 'md:grid-cols-2 max-w-3xl mx-auto' 
            : 'md:grid-cols-2 lg:grid-cols-4'
        }`}>
          {(isFreelancer ? freelancerPlanConfigs : planConfigs).map((config) => {
            const prices = getPriceDisplay(config.tier);
            const displayPrice = billingCycle === 'annual' ? prices.annual : prices.monthly;
            const discountedPrice = getDiscountedPrice(displayPrice);
            // Only show "CURRENT PLAN" badge if user is logged in
            const isCurrentPlan = user && currentTier === config.tier;
            const IconComponent = config.icon;
            
            return (
              <Card 
                key={config.tier}
                className={`relative flex flex-col bg-gradient-to-b ${config.bgGradient} ${config.borderColor} border-2 transition-all duration-300 hover:shadow-lg ${
                  isCurrentPlan ? 'ring-2 ring-blue-500 ring-offset-2' :
                  config.popular ? 'shadow-lg scale-[1.02]' : ''
                }`}
                data-testid={`plan-card-${config.tier}`}
              >
                {/* Badges */}
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full shadow-md">
                      CURRENT PLAN
                    </span>
                  </div>
                )}
                {config.popular && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full shadow-md">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <CardHeader className="pb-2 pt-6">
                  {/* Icon and Name */}
                  <div className="flex items-center gap-2 mb-1">
                    <div className="p-2 rounded-lg bg-white shadow-sm">
                      <IconComponent className={`w-5 h-5 ${
                        config.color === 'slate' ? 'text-slate-600' :
                        config.color === 'blue' ? 'text-blue-600' :
                        config.color === 'emerald' ? 'text-emerald-600' :
                        'text-purple-600'
                      }`} />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900">{config.name}</CardTitle>
                  </div>
                  
                  {/* Tagline */}
                  <p className={`text-sm font-semibold ${
                    config.color === 'slate' ? 'text-slate-600' :
                    config.color === 'blue' ? 'text-blue-600' :
                    config.color === 'emerald' ? 'text-emerald-600' :
                    'text-purple-600'
                  }`}>{config.tagline}</p>
                  <p className="text-xs text-gray-500">{config.description}</p>
                  
                  {/* Price */}
                  <div className="mt-4">
                    {config.tier === 'free' ? (
                      <div className="flex items-baseline">
                        <span className="text-4xl font-bold text-gray-900">Free</span>
                        <span className="text-gray-500 ml-2 text-sm">forever</span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-baseline flex-wrap gap-2">
                          {isPromoActive && displayPrice > 0 && (
                            <span className="text-xl text-gray-400 line-through">€{displayPrice}</span>
                          )}
                          <span className={`text-4xl font-bold ${isPromoActive ? 'text-pink-600' : 'text-gray-900'}`}>
                            €{isPromoActive ? discountedPrice : displayPrice}
                          </span>
                          <span className="text-gray-500">/mo</span>
                        </div>
                        {billingCycle === 'annual' && (
                          <p className="text-xs text-gray-500 mt-1">
                            €{isPromoActive ? getDiscountedPrice(prices.annualTotal).toLocaleString() : prices.annualTotal.toLocaleString()} billed annually
                          </p>
                        )}
                        {isPromoActive && config.tier !== 'free' && (
                          <p className="text-xs font-bold text-pink-600 mt-1">
                            🎰 SKO 2026 -{SKO_PROMO.discount}% applied!
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col pt-4">
                  {/* Best For */}
                  <div className="mb-4 p-3 bg-white/60 rounded-lg border border-white">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Best for</p>
                    <p className="text-sm text-gray-700">{config.bestFor}</p>
                  </div>

                  {/* Includes label for Advanced/Professional */}
                  {config.includes && (
                    <p className="text-xs font-medium text-gray-500 mb-2">{config.includes}</p>
                  )}

                  {/* Features List */}
                  <ul className="space-y-2 mb-3 flex-1">
                    {config.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <Check className={`w-4 h-4 mr-2 flex-shrink-0 mt-0.5 ${
                          feature.highlight ? 'text-emerald-600' : 'text-gray-400'
                        }`} />
                        <span className="text-gray-700">
                          {feature.text}
                          {feature.sub && (
                            <span className="block text-xs text-gray-500 ml-0 mt-0.5">{feature.sub}</span>
                          )}
                        </span>
                      </li>
                    ))}
                    {config.limitations && config.limitations.map((limit, idx) => (
                      <li key={`limit-${idx}`} className="flex items-start text-sm text-gray-400">
                        <X className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-gray-300" />
                        <span>{limit.text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Special Badge (Trial for Starter, AI Agents for Professional) */}
                  {config.specialBadge && (
                    <div className={`mb-3 p-2.5 rounded-lg border ${
                      config.specialBadge.color === 'blue' 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-purple-50 border-purple-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {config.specialBadge.icon && (
                            <config.specialBadge.icon className={`w-4 h-4 ${
                              config.specialBadge.color === 'blue' ? 'text-blue-600' : 'text-purple-600'
                            }`} />
                          )}
                          <span className={`text-sm font-semibold ${
                            config.specialBadge.color === 'blue' ? 'text-blue-700' : 'text-purple-700'
                          }`}>
                            {config.specialBadge.text || config.specialBadge.title}
                          </span>
                        </div>
                        {config.specialBadge.badge && (
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                            config.specialBadge.color === 'blue' 
                              ? 'bg-blue-200 text-blue-800' 
                              : 'bg-purple-200 text-purple-800'
                          }`}>
                            {config.specialBadge.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Credits Info */}
                  {config.creditsInfo && (
                    <div className="mb-4 p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-700 font-medium flex items-center gap-1">
                        <Coins className="w-3 h-3" />
                        {config.creditsInfo.label}
                      </p>
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className="mt-auto space-y-2">
                    {/* Trial button for plans with trial (Starter or Freelancer Pro) */}
                    {config.specialBadge?.title === '14-Day Free Trial' && currentTier === 'free' && !user?.trial_ends_at && (
                      <Button
                        className={`w-full shadow-md ${
                          config.popular 
                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white'
                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                        }`}
                        onClick={handleStartTrial}
                        disabled={loading === 'trial'}
                        data-testid="start-trial-btn"
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        {loading === 'trial' ? 'Starting...' : 'Start 14-day Free Trial'}
                      </Button>
                    )}

                    {/* For Starter/Freelancer Pro when user already has trial or is on higher plan, show subscribe button */}
                    {config.tier === 'starter' && !config.specialBadge?.title?.includes('Trial') && (currentTier !== 'free' || user?.trial_ends_at) && (
                      <Button
                        className={`w-full ${
                          isCurrentPlan 
                            ? 'bg-gray-100 text-gray-500 cursor-default hover:bg-gray-100' 
                            : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300'
                        }`}
                        onClick={() => !isCurrentPlan && handleSubscribe(config.tier)}
                        disabled={isCurrentPlan || loading === config.tier}
                        data-testid={`subscribe-${config.tier}-btn`}
                      >
                        {isCurrentPlan 
                          ? 'Current Plan'
                          : loading === config.tier 
                            ? 'Loading...' 
                            : 'Subscribe'}
                        {!isCurrentPlan && loading !== config.tier && (
                          <ArrowRight className="w-4 h-4 ml-2" />
                        )}
                      </Button>
                    )}

                    {/* Freelancer Pro with trial - show subscribe after trial */}
                    {config.specialBadge?.title === '14-Day Free Trial' && (currentTier !== 'free' || user?.trial_ends_at) && (
                      <Button
                        className={`w-full ${
                          isCurrentPlan 
                            ? 'bg-gray-100 text-gray-500 cursor-default hover:bg-gray-100' 
                            : config.popular 
                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md' 
                              : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300'
                        }`}
                        onClick={() => !isCurrentPlan && handleSubscribe(config.tier)}
                        disabled={isCurrentPlan || loading === config.tier}
                        data-testid={`subscribe-${config.tier}-btn`}
                      >
                        {isCurrentPlan 
                          ? 'Current Plan'
                          : loading === config.tier 
                            ? 'Loading...' 
                            : `Get ${config.name}`}
                        {!isCurrentPlan && loading !== config.tier && (
                          <ArrowRight className="w-4 h-4 ml-2" />
                        )}
                      </Button>
                    )}

                    {/* For non-Starter paid plans (Advanced, Professional) */}
                    {config.tier !== 'free' && config.tier !== 'starter' && (
                      <Button
                        className={`w-full ${
                          isCurrentPlan 
                            ? 'bg-gray-100 text-gray-500 cursor-default hover:bg-gray-100' 
                            : config.popular 
                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md' 
                              : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300'
                        }`}
                        onClick={() => !isCurrentPlan && handleSubscribe(config.tier)}
                        disabled={isCurrentPlan || loading === config.tier}
                        data-testid={`subscribe-${config.tier}-btn`}
                      >
                        {isCurrentPlan 
                          ? 'Current Plan'
                          : loading === config.tier 
                            ? 'Loading...' 
                            : `Get ${config.name}`}
                        {!isCurrentPlan && loading !== config.tier && (
                          <ArrowRight className="w-4 h-4 ml-2" />
                        )}
                      </Button>
                    )}

                    {config.tier === 'free' && !isCurrentPlan && (
                      <p className="text-center text-xs text-gray-500 py-2">
                        Default plan on signup
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Credits Section */}
        {user && currentTier !== 'free' && (
          <div className="mb-12">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Need More Activations?</h2>
              <p className="text-gray-600">Purchase credits to activate additional {itemNamePlural} beyond your plan limit</p>
            </div>
            
            <Card className="max-w-2xl mx-auto border-2 border-dashed border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50">
              <CardContent className="py-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-100 rounded-full">
                      <Coins className="w-8 h-8 text-amber-600" />
                    </div>
                    <div className="text-center sm:text-left">
                      <h3 className="text-lg font-bold text-gray-900">Activation Credits</h3>
                      <p className="text-sm text-gray-600">
                        1 credit = €1 • Use for extra activations
                      </p>
                      {user?.credit_balance > 0 && (
                        <p className="text-sm font-semibold text-amber-700 mt-1">
                          Current balance: {user.credit_balance} credits
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center border rounded-lg overflow-hidden bg-white">
                      <button 
                        className="px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                        onClick={() => setCreditAmount(Math.max(10, creditAmount - 10))}
                      >
                        −
                      </button>
                      <input 
                        type="number" 
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(Math.max(10, parseInt(e.target.value) || 10))}
                        className="w-20 text-center border-x py-2 focus:outline-none"
                        min="10"
                      />
                      <button 
                        className="px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                        onClick={() => setCreditAmount(creditAmount + 10)}
                      >
                        +
                      </button>
                    </div>
                    <Button
                      onClick={handleBuyCredits}
                      disabled={loading === 'credits'}
                      className="bg-amber-500 hover:bg-amber-600 text-white shadow-md"
                      data-testid="buy-credits-btn"
                    >
                      {loading === 'credits' ? 'Loading...' : `Buy ${creditAmount} credits`}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Enterprise CTA - Hidden for Freelancers (not relevant) */}
        {!isFreelancer && (
        <Card className="max-w-4xl mx-auto border-0 bg-gradient-to-br from-gray-900 to-gray-800 mb-12 overflow-hidden">
          <CardContent className="py-12 text-center relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(99,102,241,0.15),transparent_50%)]"></div>
            <div className="relative">
              <Crown className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
              <h2 className="text-3xl font-bold mb-4 text-white">
                Need More Than 20 Active Listings?
              </h2>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Enterprise plans with custom limits, dedicated support, and SLA guarantees.
              </p>
              <Button 
                className="bg-white text-gray-900 hover:bg-gray-100 border-0 font-semibold px-8"
                onClick={() => window.location.href = 'mailto:contact@snow-exchange.com?subject=Enterprise Plan Inquiry'}
                data-testid="contact-enterprise-btn"
              >
                Contact Sales
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
        )}

        {/* All Plans Include - Reorganized by category */}
        <div className="mb-12 max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">All Plans Include</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {commonFeaturesCategories.map((category, idx) => (
              <div key={idx} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{category.icon}</span>
                  <h4 className="font-semibold text-gray-900">{category.title}</h4>
                </div>
                <ul className="space-y-1.5">
                  {category.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ - Hidden when Freelancer is selected (they have their own FAQ) */}
        {!isFreelancer && (
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            <details className="bg-white rounded-xl border p-4 group">
              <summary className="font-medium cursor-pointer flex items-center justify-between">
                What's the difference between Passive and Proactive?
                <HelpCircle className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-3 text-gray-600 text-sm pl-1">
                <strong>Passive (Free)</strong>: Your profile is visible and others can contact you, but you cannot initiate AI-powered matching.<br/>
                <strong>Proactive (Starter+)</strong>: AI actively searches and suggests the best opportunities based on your criteria.
              </p>
            </details>
            <details className="bg-white rounded-xl border p-4 group">
              <summary className="font-medium cursor-pointer flex items-center justify-between">
                How do activation credits work?
                <HelpCircle className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-3 text-gray-600 text-sm pl-1">
                Credits let you activate {itemNamePlural} beyond your plan's limit. The cost depends on your plan: 
                Starter (100 credits), Advanced (75 credits), Professional (50 credits). 1 credit = €1.
              </p>
            </details>
            <details className="bg-white rounded-xl border p-4 group">
              <summary className="font-medium cursor-pointer flex items-center justify-between">
                Can I change or cancel my plan?
                <HelpCircle className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="mt-3 text-gray-600 text-sm pl-1">
                Yes! Upgrade anytime and the change takes effect immediately. Downgrades and cancellations take effect at the end of your billing cycle. No penalties.
              </p>
            </details>
            {isPromoActive && (
              <details className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-200 p-4 group">
                <summary className="font-medium cursor-pointer flex items-center justify-between text-pink-800">
                  How do I use the SKO 2026 discount?
                  <HelpCircle className="w-4 h-4 text-pink-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="mt-3 text-gray-600 text-sm pl-1">
                  The {SKO_PROMO.discount}% discount is automatically applied when you subscribe! You can also enter code <strong>{SKO_PROMO.code}</strong> at checkout. Valid until February 28, 2026.
                </p>
              </details>
            )}
          </div>
        </div>
        )}

        {/* Freelancer-Specific FAQ - Only shown when Freelancer tab is selected */}
        {isFreelancer && (
          <div className="max-w-3xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-purple-200"></div>
              <h2 className="text-2xl font-bold text-center text-gray-900 px-4">
                Freelancer FAQ
              </h2>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-purple-200"></div>
            </div>
            
            <div className="space-y-3">
              <details className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-4 group" open>
                <summary className="font-medium cursor-pointer flex items-center justify-between text-purple-900">
                  What's the difference between Represented and Independent?
                  <HelpCircle className="w-4 h-4 text-purple-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="mt-4 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-white rounded-lg border border-emerald-200">
                      <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Represented Freelance (Free)
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1.5">
                        <li>• Operating Partner markets your profile</li>
                        <li>• They handle negotiations & contracts</li>
                        <li>• You focus 100% on your expertise</li>
                        <li>• Keep your full Daily Rate</li>
                        <li>• No commercial effort required</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-white rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Independent Freelance (€249/mo)
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1.5">
                        <li>• Full autonomy over your business</li>
                        <li>• Direct contact with clients</li>
                        <li>• Manage your own rates & contracts</li>
                        <li>• Outbound AI matching included</li>
                        <li>• Complete control, 0% markup</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </details>
              
              <details className="bg-white rounded-xl border p-4 group">
                <summary className="font-medium cursor-pointer flex items-center justify-between">
                  Who should choose Represented Freelance?
                  <HelpCircle className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="mt-3 text-gray-600 text-sm space-y-2">
                  <p><strong>Ideal for:</strong></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Consultants who prefer focusing on delivery over sales</li>
                    <li>Freelancers new to the ServiceNow ecosystem</li>
                    <li>Those who want visibility without managing pipelines</li>
                    <li>Professionals between contracts who want passive exposure</li>
                  </ul>
                  <p className="mt-2 p-2 bg-emerald-50 rounded text-emerald-800 text-xs">
                    <strong>Example:</strong> Sophie, a ServiceNow Developer, wants to focus on coding. With Represented, SCS Consulting markets her profile while she delivers projects.
                  </p>
                </div>
              </details>
              
              <details className="bg-white rounded-xl border p-4 group">
                <summary className="font-medium cursor-pointer flex items-center justify-between">
                  Who should choose Independent Freelance?
                  <HelpCircle className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="mt-3 text-gray-600 text-sm space-y-2">
                  <p><strong>Ideal for:</strong></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Experienced freelancers with established networks</li>
                    <li>Consultants who enjoy business development</li>
                    <li>Those who want full control over client negotiations</li>
                    <li>Freelancers who want AI-powered outbound matching</li>
                  </ul>
                  <p className="mt-2 p-2 bg-blue-50 rounded text-blue-800 text-xs">
                    <strong>Example:</strong> Marc, a senior Architect, already has client relationships. With Independent, he uses AI matching to find new opportunities while managing his own rates.
                  </p>
                </div>
              </details>
              
              <details className="bg-white rounded-xl border p-4 group">
                <summary className="font-medium cursor-pointer flex items-center justify-between">
                  What is the Operating Partner?
                  <HelpCircle className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="mt-3 text-gray-600 text-sm">
                  The Operating Partner (currently <strong>SCS Schnittker Consulting</strong>) is a trusted ServiceNow partner who actively markets Represented Freelancers to their network of clients. They handle commercial negotiations and take a margin on the client side — but your rate stays 100% yours.
                </p>
              </details>
              
              <details className="bg-white rounded-xl border p-4 group">
                <summary className="font-medium cursor-pointer flex items-center justify-between">
                  Can I change or cancel my plan?
                  <HelpCircle className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="mt-3 text-gray-600 text-sm">
                  <strong>Independent → Represented:</strong> You have a 24-hour grace period after choosing Represented. After that, contact support.<br/><br/>
                  <strong>Represented → Independent:</strong> Upgrade anytime by subscribing to the Independent plan.<br/><br/>
                  <strong>Cancel subscription:</strong> Takes effect at the end of your billing cycle. No penalties, cancel anytime.
                </p>
              </details>
              
              {isPromoActive && (
                <details className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-200 p-4 group">
                  <summary className="font-medium cursor-pointer flex items-center justify-between text-pink-800">
                    How do I use the SKO 2026 discount?
                    <HelpCircle className="w-4 h-4 text-pink-400 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="mt-3 text-gray-600 text-sm">
                    The {SKO_PROMO.discount}% discount is automatically applied when you subscribe to Independent Freelance! You can also enter code <strong>{SKO_PROMO.code}</strong> at checkout. Valid until February 28, 2026.
                  </p>
                </details>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PricingPage;
